import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Lock, ShieldCheck, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12 bg-grid">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Lock className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ResumeLock</span>
        </Link>
        <Card className="p-8 shadow-sm">
          {sent ? (
            <div className="text-center" data-testid="forgot-sent-state">
              <div className="w-12 h-12 mx-auto rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <MailCheck className="w-6 h-6" />
              </div>
              <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Check your inbox</h1>
              <p className="text-sm text-slate-500 mt-2">
                If an account exists for <span className="font-medium text-slate-700">{email}</span>, you'll get an email with a reset link within a minute.
              </p>
              <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-medium text-blue-600 hover:underline">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-bold tracking-tight">Forgot your password?</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your email and we'll send a secure reset link.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <Input
                    id="email" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="forgot-email-input"
                    className="mt-1.5"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 h-11" data-testid="forgot-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reset link"}
                </Button>
              </form>
              <div className="mt-6 text-center text-sm text-slate-500">
                Remembered it? <Link to="/login" className="text-blue-600 font-medium hover:underline">Back to login</Link>
              </div>
            </>
          )}
        </Card>
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
          Reset links are single-use and expire in 1 hour.
        </div>
      </div>
    </div>
  );
}
